import React, { useCallback } from 'react';

import { useIntl } from 'react-intl';

import SearchIcon from '@atlaskit/icon/core/search';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { highlightMatchedText } from '@atlassian/search-common/highlight-matched-text';

import { SearchLink } from '../../../../../common/ui/search-link';
import {
	type EventWithTarget,
	type SearchDropdownElementProps,
	SearchLinkTypes,
} from '../../../../../common/ui/search-link/types';
import { onDropdownResultClicked } from '../../../../../common/utils/events/dropdown-result-clicked';
import { useAnalytics, useCommonAndPrivateAttributes } from '../../../../../controllers/analytics';
import {
	useAutoSuggestionResults,
	useDropdown,
	useRecentSearchResults,
	useResultCount,
} from '../../../../../controllers/store/dropdown';
import { useInputQuery } from '../../../../../controllers/store/search';

import { messages } from './messages';

const iconStyles = xcss({
	backgroundColor: 'color.background.neutral',
	borderRadius: 'radius.xsmall',
	width: '24px',
	height: '24px',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});

export const AutoSuggestion = ({
	getElementIndex,
}: SearchDropdownElementProps): React.JSX.Element => {
	const [inputQuery, { setSearchQuery }] = useInputQuery();
	const [, { setShowDropdown }] = useDropdown();
	const intl = useIntl();

	const [recentSearchResults] = useRecentSearchResults();
	const [autoSuggestionResults] = useAutoSuggestionResults();
	const [resultCount] = useResultCount();

	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useCommonAndPrivateAttributes();

	const recentSearchTitles = recentSearchResults
		.slice(0, resultCount.recentSearch)
		.map((search) => search.title.toLowerCase());
	const filteredAutoSuggestions = autoSuggestionResults.results
		.filter((suggestion) => {
			return (
				!recentSearchTitles.includes(suggestion.title.toLowerCase()) &&
				suggestion.title !== inputQuery.toLowerCase()
			);
		})
		.slice(0, resultCount.autoSuggestion);

	const fireAnalytics = useCallback(
		(event: EventWithTarget) => {
			const index = event.target && getElementIndex ? getElementIndex(event.target) : undefined;

			fireAnalyticsEvent(
				onDropdownResultClicked({
					commonAttributes,
					nonPrivacySafeAttributes,
					resultType: SearchLinkTypes.AutoSuggestion,
					duration: autoSuggestionResults.duration,
					...(index !== undefined ? { index } : {}),
				}),
			);
		},
		[
			getElementIndex,
			fireAnalyticsEvent,
			commonAttributes,
			nonPrivacySafeAttributes,
			autoSuggestionResults.duration,
		],
	);

	return (
		<>
			{filteredAutoSuggestions.map((suggestion, index) => (
				<SearchLink
					title={suggestion.title || ''}
					icon={
						<Box xcss={iconStyles}>
							<SearchIcon
								color="currentColor"
								label={intl.formatMessage(messages.searchIconLabel)}
							/>
						</Box>
					}
					onClick={(event) => {
						setSearchQuery(suggestion.title || '');
						setShowDropdown(false);
						fireAnalytics(event);
					}}
					highlightedTitle={
						highlightMatchedText(suggestion.title, inputQuery.split(' ')).negatedHighlightedTitle
					}
					key={`${index}-${suggestion.title}`}
				/>
			))}
		</>
	);
};
